import axios, { AxiosProgressEvent, CancelTokenSource } from 'axios'
import { useState } from 'react'

import { CHUNK_SIZE, FileUploaderClient } from '@/pages/data-mgr/file-client.ts'

import boxApi from './api'

export enum UPLOAD_RESPONSE_CODE {
  SUCCESS = 0,
  FILE_ALREADY_EXISTS = 1,
  DATADRIVE_FULL = 17,
}

export enum FILE_STATUS {
  PENDING,
  PREPARING,
  UPLOADING,
  PAUSED, // TODO
  SUCCESS,
  FAILED,
  RETRYING,
}

interface ITask {
  index: number
  file: File
  status: FILE_STATUS
  loaded: number
  name: string
  size: number
  cancelSource: CancelTokenSource
}

export function useUpload() {
  const [uploadList, setUploadList] = useState<ITask[]>([])
  const [successNum, setSuccessNum] = useState(0)

  const createTask = (file: File) => {
    const task: ITask = {
      index: -1,
      file,
      status: FILE_STATUS.UPLOADING,
      loaded: 0,
      name: file.name,
      size: file.size,
      cancelSource: axios.CancelToken.source(),
    }

    setUploadList((queue) => {
      task.index = queue.length
      return [...queue, task]
    })

    return task
  }
  const updateTask = (task: ITask, state: Partial<ITask>) => {
    if (task.index < 0) {
      return
    }

    Object.assign(task, state)
    setUploadList((queue) => [...queue])
  }

  const uploadFile = async (file: File, target: string, domain: number) => {
    const task = createTask(file)

    const onUploadProgress = (e: AxiosProgressEvent) => {
      updateTask(task, { loaded: e.loaded })
    }

    if (file.size > CHUNK_SIZE * 4) {
      let blocks = 0
      const chunkMap: { [key: number]: boolean } = {}

      const client = new FileUploaderClient({
        chunkSize: CHUNK_SIZE,
        requestOptions: {
          retryTimes: 3,
          initFilePartUploadFunc: async () => {},
          uploadPartFileFunc: async (options) => {
            if (chunkMap[options.index]) {
              // skip if already uploaded
              return Promise.resolve().then(() => {
                blocks = blocks + 1
                onUploadProgress({
                  loaded: blocks * CHUNK_SIZE,
                  bytes: blocks * CHUNK_SIZE,
                  lengthComputable: true,
                } as AxiosProgressEvent)
              })
            }
            const formData = new FormData()
            formData.append('file', options.chunk)
            formData.append('file_name', `${target}${file.name}`)
            formData.append('file_size', file.size.toString())
            formData.append('checksum', options.fileMd5)
            formData.append('block_index', options.index.toString())
            formData.append('block_size', CHUNK_SIZE.toString())
            formData.append('checksum_alg', '1')

            const { code } = await boxApi.post('/vol/block_file', formData, {
              domain,
              timeout: 0,
              noErrMsg: true,
            })

            if (code === UPLOAD_RESPONSE_CODE.FILE_ALREADY_EXISTS) {
              updateTask(task, {
                status: FILE_STATUS.SUCCESS,
                loaded: file.size,
              })
              client.cancel()
            }

            if (code === UPLOAD_RESPONSE_CODE.DATADRIVE_FULL) {
              updateTask(task, {
                status: FILE_STATUS.FAILED,
              })
              client.cancel()
            }

            if (code === UPLOAD_RESPONSE_CODE.SUCCESS) {
              chunkMap[options.index] = true
              blocks = blocks + 1
              onUploadProgress({
                loaded: blocks * CHUNK_SIZE,
                bytes: blocks * CHUNK_SIZE,
                lengthComputable: true,
              } as AxiosProgressEvent)
            }
          },
          finishFilePartUploadFunc: async () => {
            updateTask(task, { status: FILE_STATUS.SUCCESS })
          },
        },
      })

      try {
        await client.uploadFile(file)
        setSuccessNum((num) => num + 1)
      } catch (e) {
        console.error(e)
      }
      return
    }

    const data = new FormData()
    data.append('file', file)
    const params = { path: `${target}${file.name}` }

    const { code } = await boxApi.post('/vol', data, {
      domain,
      timeout: 0,
      params,
      onUploadProgress,
      cancelToken: task.cancelSource.token,
      noErrMsg: true,
    })

    updateTask(task, { status: code === 0 ? FILE_STATUS.SUCCESS : FILE_STATUS.FAILED })
    if (code === 0) {
      setSuccessNum((num) => num + 1)
    }
  }

  return [uploadList, uploadFile, successNum] as const
}
